import { IPurchaseRequestService, CreatePurchaseRequestDTO } from '../api/PurchaseRequestService';
import {
  PurchaseRequest,
  DocumentAttachment,
  RequestComment,
  NoteForApproval,
  LetterForAward,
  BudgetSummary,
  DocumentCategory,
} from '@/types/domain';
import { MOCK_PURCHASE_REQUESTS, MOCK_NFA, MOCK_LFA } from '@/mocks/database';
import { processQuery, getEntityById } from './pagination';
import { env } from '@/config/env';
import { notifyGlobal } from '@/context/NotificationContext';
import { mockApprovalService } from './MockApprovalService';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export class MockPurchaseRequestService implements IPurchaseRequestService {
  private requests: PurchaseRequest[] = [...MOCK_PURCHASE_REQUESTS];
  private nfas: NoteForApproval[] = [...MOCK_NFA];
  private lfas: LetterForAward[] = [...MOCK_LFA];
  private documents: Record<string, DocumentAttachment[]> = {};
  private comments: Record<string, RequestComment[]> = {};
  private submittedKeys = new Set<string>();
  private createListeners: ((pr: PurchaseRequest) => Promise<void> | void)[] = [];

  onRequestCreated(callback: (pr: PurchaseRequest) => Promise<void> | void) {
    this.createListeners.push(callback);
  }

  findRequestInMemory(idOrNumber: string): PurchaseRequest | undefined {
    if (!idOrNumber) return undefined;
    const target = String(idOrNumber).trim().toLowerCase();
    const digits = target.replace(/[^0-9]/g, '');

    // 1. Direct case-insensitive match on id or requestNumber
    const exact = this.requests.find(
      (r) =>
        String(r.id || '').toLowerCase() === target ||
        String(r.requestNumber || '').toLowerCase() === target
    );
    if (exact) return exact;

    // 2. Numeric sequence match (e.g., '9272' matches REQ-9272, req-9272, PR-9272)
    if (digits) {
      const byDigits = this.requests.find((r) => {
        const rDigits = String(r.requestNumber || r.id || '').replace(/[^0-9]/g, '');
        return rDigits === digits || rDigits.endsWith(digits) || digits.endsWith(rDigits);
      });
      if (byDigits) return byDigits;
    }

    return undefined;
  }

  constructor() {
    mockApprovalService.onApprovalChange((record, action) => {
      const target = this.requests.find(
        (r) => r.id === record.id || r.requestNumber === record.relatedEntityId
      );
      if (target) {
        if (action === 'APPROVED') {
          target.status = 'Approved';
          if (target.approvalChain && target.approvalChain[0]) {
            target.approvalChain[0].status = 'Approved';
            target.approvalChain[0].actionDate = new Date().toISOString();
          }
        } else if (action === 'REJECTED') {
          target.status = 'Rejected';
          if (target.approvalChain && target.approvalChain[0]) {
            target.approvalChain[0].status = 'Rejected';
            target.approvalChain[0].actionDate = new Date().toISOString();
          }
        }
      }
    });
  }

  async getAllRequests(): Promise<PurchaseRequest[]> {
    await delay(env.mockDelayMs);
    return [...this.requests];
  }

  async getApprovalQueue(): Promise<PurchaseRequest[]> {
    await delay(env.mockDelayMs);
    return this.requests.filter(
      (r) => r.status === 'Submitted' || r.status === 'PendingApproval' || r.status === 'UnderReview'
    );
  }

  async getRequests(params?: {
    status?: string;
    category?: string;
    search?: string;
    page?: number;
    pageSize?: number;
    scope?: {
      ownership?: 'OWN' | 'DEPARTMENT' | 'PLANT' | 'ENTERPRISE';
      userId?: string;
      userName?: string;
      departments?: string[];
      plants?: string[];
    };
  }): Promise<{ requests: PurchaseRequest[]; total: number }> {
    // Ultra-fast response for interactive search and category filtering
    const searchDelay = params?.search || (params?.category && params.category !== 'ALL') || (params?.status && params.status !== 'ALL') ? 20 : 50;
    await delay(searchDelay);

    let sourceData = [...this.requests];
    // Exclude Drafts from general request table so drafts only appear in the drafts page / tab
    if (!params?.status || params.status === 'ALL') {
      sourceData = sourceData.filter((r) => r.status !== 'Draft');
    }

    // Enterprise Scope Filtering
    if (params?.scope) {
      const { ownership, userId, userName, departments, plants } = params.scope;
      if (ownership === 'OWN') {
        sourceData = sourceData.filter(
          (r) =>
            (userId && r.requesterId === userId) ||
            (userName && r.requesterName?.toLowerCase().includes(userName.toLowerCase()))
        );
      } else if (ownership === 'DEPARTMENT' && departments && departments.length > 0) {
        sourceData = sourceData.filter((r) => r.departmentCode && departments.includes(r.departmentCode));
      } else if (ownership === 'PLANT' && plants && plants.length > 0) {
        sourceData = sourceData.filter((r) => r.plantCode && plants.includes(r.plantCode));
      }
    }

    const queryResult = processQuery(sourceData, {
      page: params?.page || 1,
      pageSize: params?.pageSize || 10,
      search: params?.search,
      searchFields: ['requestNumber', 'title', 'requesterName', 'departmentName', 'category', 'status'],
      filters: {
        status: params?.status,
        category: params?.category,
      },
    });

    return { requests: queryResult.data, total: queryResult.total };
  }

  async getRequestById(id: string): Promise<PurchaseRequest | null> {
    await delay(env.mockDelayMs);
    const found = getEntityById(this.requests, id, 'id') || getEntityById(this.requests, id, 'requestNumber');
    if (found) return found;
    if (this.requests.length > 0) {
      const fallback = Object.assign({}, this.requests[0], { id, requestNumber: id }) as PurchaseRequest;
      this.requests.unshift(fallback);
      return fallback;
    }
    return null;
  }

  async approveRequest(
    id: string,
    comments?: string,
    approver?: { id?: string; name?: string; role?: string; approvalAuthorityLimit?: number }
  ): Promise<PurchaseRequest> {
    await delay(env.mockDelayMs);
    const req = await this.getRequestById(id);
    if (!req) throw new Error('Request not found');

    if (approver) {
      // 1. Employee cannot approve own request (Segregation of Duties)
      if (
        approver.role === 'EMPLOYEE' ||
        (approver.id && req.requesterId === approver.id) ||
        (approver.name && req.requesterName.toLowerCase() === approver.name.toLowerCase())
      ) {
        throw new Error('Segregation of Duties violation: Employees cannot approve their own procurement requests.');
      }

      // 2. Cannot approve above DOA authority limit
      if (
        approver.approvalAuthorityLimit !== undefined &&
        approver.role !== 'ADMIN' &&
        req.totalAmount > approver.approvalAuthorityLimit
      ) {
        throw new Error(
          `DOA Authorization Cap Exceeded: Total requisition amount ($${req.totalAmount.toLocaleString()}) exceeds your approval limit ($${approver.approvalAuthorityLimit.toLocaleString()}).`
        );
      }
    }

    if (req.status === 'Approved') {
      throw new Error('Requisition has already been approved.');
    }
    if (req.status === 'Rejected' || req.status === 'Cancelled') {
      throw new Error(`Cannot approve requisition in "${req.status}" state.`);
    }

    const updatedChain = req.approvalChain.map((step) =>
      step.stepNumber === 1
        ? { ...step, status: 'Approved' as const, actionDate: new Date().toISOString(), comments: comments || `Approved by ${approver?.name || 'Manager'}` }
        : step
    );

    req.status = 'Approved';
    req.approvalChain = updatedChain;
    req.updatedAt = new Date().toISOString();

    mockApprovalService.syncPurchaseRequest(req);

    notifyGlobal(
      `Requisition ${req.requestNumber} Approved`,
      `${approver?.name || 'Manager Marcus Vance'} approved requisition "${req.title}" ($${req.totalAmount.toLocaleString()}).`,
      'StatusChange',
      req.id
    );

    return req;
  }

  async rejectRequest(id: string, comments?: string): Promise<PurchaseRequest> {
    await delay(env.mockDelayMs);
    const req = await this.getRequestById(id);
    if (!req) throw new Error('Request not found');

    const updatedChain = req.approvalChain.map((step) =>
      step.stepNumber === 1
        ? { ...step, status: 'Rejected' as const, actionDate: new Date().toISOString(), comments: comments || 'Rejected by Manager' }
        : step
    );

    req.status = 'Rejected';
    req.approvalChain = updatedChain;
    req.updatedAt = new Date().toISOString();

    mockApprovalService.syncPurchaseRequest(req);

    notifyGlobal(
      `Requisition ${req.requestNumber} Rejected`,
      `Manager Marcus Vance rejected requisition "${req.title}". Reason: ${comments || 'No comment provided'}.`,
      'StatusChange',
      req.id
    );

    return req;
  }

  async createRequest(data: CreatePurchaseRequestDTO): Promise<PurchaseRequest> {
    return this.createPurchaseRequest(data);
  }

  async createPurchaseRequest(data: CreatePurchaseRequestDTO): Promise<PurchaseRequest> {
    await delay(env.mockDelayMs);

    if (data.idempotencyKey) {
      if (this.submittedKeys.has(data.idempotencyKey)) {
        const existing = this.requests.find((r) => r.idempotencyKey === data.idempotencyKey);
        if (existing) return existing;
      }
      this.submittedKeys.add(data.idempotencyKey);
    }

    const totalCalc = (data.items || []).reduce(
      (acc, item) => acc + (item.totalEstimatedPrice || item.quantity * item.estimatedUnitPrice || 0),
      0
    );
    const seq = Math.floor(1000 + Math.random() * 9000);
    const reqNum = `REQ-${seq}`;

    const newReq: PurchaseRequest = {
      id: `req-${seq}`,
      requestNumber: reqNum,
      title: data.title,
      requesterId: data.requesterId || 'usr-101',
      requesterName: data.requesterName || 'Sarah Jenkins',
      departmentId: data.departmentId || 'd1',
      departmentCode: data.departmentCode || 'DEP-IT',
      departmentName: data.departmentName || 'Information Technology',
      plantId: data.plantId || 'p1',
      plantCode: data.plantCode || 'PLANT-1000',
      costCenterId: data.costCenterId || 'cc1',
      costCenterCode: data.costCenterCode || 'CC-IT-101',
      category: data.category || 'CapitalEquipment',
      procurementType: (data as any).procurementType || ((data.category as string) === 'Service' || (data as any).procurementType === 'Service' ? 'Service' : 'Material'),
      serviceDetails: (data as any).serviceDetails || ((data as any).procurementType === 'Service' || (data.category as string) === 'Service' ? { serviceName: 'Cyber Security Operations Audit', serviceProvider: 'KPMG Advisory' } : undefined),
      justification: data.justification || 'Departmental procurement requirement',
      totalAmount: totalCalc,
      currency: data.currency || 'USD',
      budgetCheckPassed: true,
      status: data.idempotencyKey ? 'PendingApproval' : 'Submitted',
      idempotencyKey: data.idempotencyKey,
      items: (data.items || []).map((item, idx) => ({
        id: `item-${seq}-${idx + 1}`,
        requestId: `req-${seq}`,
        itemNumber: (idx + 1) * 10,
        materialCode: item.materialCode || `MAT-900${idx + 1}`,
        materialName: item.materialName || item.materialCode || 'Hardware Spec',
        category: data.category || 'IT Hardware',
        quantity: item.quantity,
        unitOfMeasure: item.unitOfMeasure || 'EA',
        estimatedUnitPrice: item.estimatedUnitPrice,
        totalEstimatedPrice: item.totalEstimatedPrice || item.quantity * item.estimatedUnitPrice,
        currency: data.currency || 'USD',
        requiredDate: data.requiredDate || '2026-10-30',
        specifications: item.specifications || 'Standard enterprise spec',
        costCenterCode: data.costCenterCode || 'CC-IT-101',
        plantCode: data.plantCode || 'PLANT-1000',
        glAccountCode: '500100',
      })),
      approvalChain: [
        { id: `app-${seq}-1`, stepNumber: 1, approverRole: 'Department Manager', approverName: 'Marcus Vance', status: 'Pending' },
        { id: `app-${seq}-2`, stepNumber: 2, approverRole: 'Finance Controller', approverName: 'Rachel Chen', status: 'Pending' },
      ],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    this.requests.unshift(newReq);
    mockApprovalService.syncPurchaseRequest(newReq);

    for (const listener of this.createListeners) {
      try {
        await listener(newReq);
      } catch (err) {
        console.error('Error executing onRequestCreated listener:', err);
      }
    }

    notifyGlobal('Purchase Requisition Submitted', `Requisition ${newReq.requestNumber} (${newReq.title}) submitted.`, 'StatusChange', newReq.id);
    return newReq;
  }

  async saveDraft(data: Partial<CreatePurchaseRequestDTO>): Promise<PurchaseRequest> {
    await delay(env.mockDelayMs);
    const seq = Math.floor(1000 + Math.random() * 9000);
    const reqNum = `DRAFT-${seq}`;

    const draft: PurchaseRequest = {
      id: `req-${seq}`,
      requestNumber: reqNum,
      title: data.title || 'Draft Purchase Request',
      requesterId: 'usr-1',
      requesterName: 'Sarah Jenkins',
      departmentId: 'd1',
      departmentCode: 'DEP-IT',
      departmentName: 'Information Technology',
      plantId: 'p1',
      plantCode: 'PLANT-1000',
      costCenterId: 'cc1',
      costCenterCode: 'CC-IT-101',
      category: data.category || 'CapitalEquipment',
      justification: data.justification || 'Draft justification',
      totalAmount: 0,
      currency: 'USD',
      budgetCheckPassed: true,
      status: 'Draft',
      items: [],
      approvalChain: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    this.requests.unshift(draft);
    return draft;
  }

  async deleteDraft(id: string): Promise<boolean> {
    return this.deleteRequest(id);
  }

  async updateRequest(
    id: string,
    updates: Partial<PurchaseRequest>,
    actor?: { id?: string; name?: string; role?: string }
  ): Promise<PurchaseRequest> {
    await delay(env.mockDelayMs);
    const req = await this.getRequestById(id);
    if (!req) throw new Error('Request not found');

    if (actor && actor.role === 'EMPLOYEE') {
      if (req.status !== 'Draft') {
        throw new Error('Submitted requisitions are locked and cannot be modified by employees.');
      }
      if (
        (actor.id && req.requesterId !== actor.id) ||
        (actor.name && req.requesterName.toLowerCase() !== actor.name.toLowerCase())
      ) {
        throw new Error('Unauthorized: You can only edit your own requisitions.');
      }
    }

    Object.assign(req, updates, { updatedAt: new Date().toISOString() });
    return req;
  }

  async updateRequestStatus(id: string, status: any, _comment?: string): Promise<PurchaseRequest> {
    return this.updateRequest(id, { status });
  }

  async submitRequest(id: string, commentOrIdempotencyKey?: string): Promise<PurchaseRequest> {
    const req = await this.getRequestById(id);
    if (req) {
      if (req.requestNumber.startsWith('DRAFT-')) {
        const seq = Math.floor(1000 + Math.random() * 9000);
        req.requestNumber = `REQ-${seq}`;
      }
      req.status = 'PendingApproval';
      req.updatedAt = new Date().toISOString();
      if (commentOrIdempotencyKey) {
        req.idempotencyKey = commentOrIdempotencyKey;
      }
      mockApprovalService.syncPurchaseRequest(req);
      return req;
    }
    const updated = await this.updateRequest(id, { status: 'PendingApproval' });
    mockApprovalService.syncPurchaseRequest(updated);
    return updated;
  }

  async deleteRequest(id: string, actor?: { id?: string; name?: string; role?: string }): Promise<boolean> {
    await delay(30);
    const idx = this.requests.findIndex((r) => r.id === id || r.requestNumber === id);
    if (idx !== -1) {
      const target = this.requests[idx]!;
      if (actor && actor.role === 'EMPLOYEE') {
        if (target.status !== 'Draft') {
          throw new Error('Employees can only delete requisitions in Draft status.');
        }
        if (
          (actor.id && target.requesterId !== actor.id) ||
          (actor.name && target.requesterName.toLowerCase() !== actor.name.toLowerCase())
        ) {
          throw new Error('Unauthorized: You can only delete your own draft requisitions.');
        }
      }
      this.requests.splice(idx, 1);
      return true;
    }
    return false;
  }

  async duplicateRequest(id: string): Promise<PurchaseRequest> {
    await delay(env.mockDelayMs);
    const original = await this.getRequestById(id);
    if (!original) throw new Error('Original request not found');

    const seq = Math.floor(1000 + Math.random() * 9000);
    const dup: PurchaseRequest = {
      ...original,
      id: `req-${seq}`,
      requestNumber: `REQ-${seq}`,
      title: `Copy of ${original.title}`,
      status: 'Draft',
      sapPrNumber: undefined,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    this.requests.unshift(dup);
    return dup;
  }

  async getNfas(): Promise<NoteForApproval[]> {
    await delay(env.mockDelayMs);
    return [...this.nfas];
  }

  async getNfaById(id: string): Promise<NoteForApproval | null> {
    await delay(env.mockDelayMs);
    return this.nfas.find((n) => n.id === id || n.nfaNumber === id) || null;
  }

  async createNfa(data: Partial<NoteForApproval>): Promise<NoteForApproval> {
    await delay(env.mockDelayMs);
    const seq = Math.floor(1000 + Math.random() * 9000);
    const newNfa: NoteForApproval = { ...this.nfas[0], id: `nfa-${seq}`, nfaNumber: `NFA-${seq}`, ...data } as any;
    this.nfas.unshift(newNfa);
    return newNfa;
  }

  async getLfas(): Promise<LetterForAward[]> {
    await delay(env.mockDelayMs);
    return [...this.lfas];
  }

  async getLfaById(id: string): Promise<LetterForAward | null> {
    await delay(env.mockDelayMs);
    return this.lfas.find((l) => l.id === id || l.lfaNumber === id) || null;
  }

  async addComment(requestId: string, text: string, _attachmentId?: string): Promise<RequestComment> {
    await delay(env.mockDelayMs);
    const comment: RequestComment = {
      id: `comment-${Date.now()}`,
      requestId,
      userId: 'usr-1',
      userName: 'Sarah Jenkins',
      userRole: 'Requisitioner',
      commentText: text,
      createdAt: new Date().toISOString(),
    };
    if (!this.comments[requestId]) this.comments[requestId] = [];
    this.comments[requestId].push(comment);
    return comment;
  }

  async respondClarification(id: string, responseText: string): Promise<PurchaseRequest> {
    await delay(env.mockDelayMs);
    const req = await this.getRequestById(id);
    if (!req) throw new Error('Request not found');

    req.status = 'UnderReview';
    req.updatedAt = new Date().toISOString();

    notifyGlobal(
      `Clarification Submitted for ${req.requestNumber}`,
      `Requisitioner responded: "${responseText.slice(0, 40)}...". Workflow resumed.`,
      'StatusChange',
      req.id
    );

    return req;
  }

  async respondToClarification(id: string, arg2: string, arg3?: string): Promise<PurchaseRequest> {
    const text = arg3 || arg2;
    return this.respondClarification(id, text);
  }

  async uploadDocument(requestId: string, file: File | any, docCategory?: string): Promise<DocumentAttachment> {
    await delay(env.mockDelayMs);
    const cat = (docCategory || 'TechnicalSpec') as DocumentCategory;
    const doc: DocumentAttachment = {
      id: `doc-${Date.now()}`,
      requestId,
      fileName: file?.name || 'specs.pdf',
      fileSize: file?.size || 1024,
      fileType: file?.type || 'application/pdf',
      uploadedBy: 'usr-1',
      uploadedByName: 'Sarah Jenkins',
      uploadedAt: new Date().toISOString(),
      url: 'https://procureai.enterprise/docs/sample.pdf',
      category: cat,
    };
    if (!this.documents[requestId]) this.documents[requestId] = [];
    this.documents[requestId].push(doc);
    return doc;
  }

  async getRequestDocuments(requestId: string): Promise<DocumentAttachment[]> {
    await delay(env.mockDelayMs);
    return this.documents[requestId] || [
      {
        id: 'DOC-101',
        requestId,
        fileName: 'Technical_Specification_V2.pdf',
        fileSize: 2450000,
        fileType: 'application/pdf',
        uploadedBy: 'usr-1',
        uploadedByName: 'Sarah Jenkins',
        uploadedAt: new Date().toISOString(),
        url: 'https://procureai.enterprise/docs/sample.pdf',
        category: 'TechnicalSpec' as DocumentCategory,
      },
    ];
  }

  async deleteDocument(_documentId: string): Promise<boolean> {
    await delay(env.mockDelayMs);
    return true;
  }

  async getBudget(costCenterCode: string): Promise<BudgetSummary> {
    await delay(env.mockDelayMs);
    return {
      costCenterCode: costCenterCode || 'CC-ENG-402',
      budgetLimit: 1000000,
      budgetUsed: 350000,
      pendingRequests: 50000,
      availableBudget: 600000,
      currency: 'INR',
    };
  }
}

export const mockPurchaseRequestService = new MockPurchaseRequestService();
